function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

function _defineProperty(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

import cn from 'classnames';
import React, { useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useHistory } from 'react-router-dom';
import { Button, Checkbox, FullPageModal, FullPageModalAdditionalActions, FullPageModalFooterEnd, FullPageModalFooterStart, FullPageModalHeaderStart, Grid, MenuItem, ScrollToTopButton, TextField, Tooltip, Typography, WithInfoIcon, useDidUpdate } from 'seagull';
import ErrorBoundaryPage from "../ErrorBoundaryPage";
import { businessSettingsDefaults, netTermsOptions, routes } from "../../constants";
import { useStore, useStoreActions } from "../../contexts";
import { useIsMounted } from "../../hooks/useIsMounted";
import { wait, scrollIntoView, numberAndUnit } from "../../infra/utils";
import { amendmentsApprovalMethodModel, legalTermsModel, serviceModel } from "../../models";
import { RequestAccountantAccess, requestAccountantAccessModel } from "../../models/request-accountant-access";
import { accountantAccessInfoTooltip } from "../../business-logic/accountantAccessLogic";
import styles from "./AgreementStudio.module.scss";
import { defaultCoverLetterNewClient } from "./AgreementStudio.utils";
import ExtraClause from "./ExtraClause";
import FormlingContainer from "./FormlingContainer";
import ServicePackages from "./ServicePackages/ServicePackages";
import LegalTermsDropdown from "../LegalTermsDropdown/LegalTermsDropdown";
import IntroMessage from "./IntroMessage/IntroMessage";
import ConditionalWrapper from "../ConditionalWrapper/ConditionalWrapper";
import compact from 'lodash/compact';
import { useSignal } from "../../hooks/useSignal";
import { defaultWidth, AllowManualPaymentsCheckbox, CoverCreditCardFeesSelector, AmendmentsApprovalMethodField } from "../agreement-controllers";
import { useFeatureFlags } from "../../hooks/useFeatureFlags";
import { isAmendmentAutoApprovalValid } from "../../business-logic/amendmentsAutoApproval";

const InnerSep = props => /*#__PURE__*/React.createElement("div", {
  className: cn(styles.innerSeparator, {
    [styles.thin]: props.thin
  })
});

export default function AgreementStudioWithLegalTermsModal(props) {
  var _relationshipDraft$co, _relationshipDraft$le, _relationshipDraft$le2, _relationshipDraft$le3, _relationshipDraft$le4, _worksAt$settings, _worksAt$settings2, _worksAt$settings3, _worksAt$settings4, _worksAt$settings4$fe, _worksAt$settings7;

  const {
    relationshipDraft,
    legalTermsDocuments,
    location,
    worksAt,
    autoSaveTimeout = 1500,
    isSampleRelationship = false,
    agreementTitleMP,
    savingStatusMP,
    modalTitle,
    additionalAutoSaveDependencies = [],
    inlineFooterActions,
    additionalFooterActions,
    submitButtonLabel,
    submitButtonIntent,
    doScrollToTopRef,
    contact,
    onSubmitCb,
    onCloseCb,
    onAutoSaveCb,
    openPreviewModal,
    onUploadLegalTermsDocument
  } = props;
  const history = useHistory();
  const {
    logBI
  } = useStoreActions();
  const {
    COB_IMPROVEMENTS,
    EXTEND_AMENDMENTS_AUTO_APPROVAL = false
  } = useFeatureFlags();
  const isMounted = useIsMounted();
  const contacts = useStore(state => state.contacts);
  const {
    disableScrollAnimation
  } = useStore(state => state.appConfig);
  const [wasSubmitted, setWasSubmitted] = useState(false);
  const includingExpenses = false;
  const isExistingCustomer = Boolean(relationshipDraft === null || relationshipDraft === void 0 ? void 0 : relationshipDraft.isExistingCustomer);
  const coverLetterValid = true;
  const coverLetter = (_relationshipDraft$co = relationshipDraft === null || relationshipDraft === void 0 ? void 0 : relationshipDraft.coverLetter) !== null && _relationshipDraft$co !== void 0 ? _relationshipDraft$co : defaultCoverLetterNewClient;
  const [services, setServices] = useState(() => relationshipDraft ? relationshipDraft.services : []);
  const [bundles, setBundles] = useState(() => relationshipDraft && relationshipDraft.serviceBundles ? relationshipDraft.serviceBundles.bundles.map(b => _objectSpread(_objectSpread({}, b), {}, {
    services: b.services
  })) : []);
  const availableLegalDocs = useMemo(() => {
    if (legalTermsDocuments.some(_ref => {
      let {
        id
      } = _ref;
      return (relationshipDraft === null || relationshipDraft === void 0 ? void 0 : relationshipDraft.legalTerms) != null && 'libraryLegalTermsId' in (relationshipDraft === null || relationshipDraft === void 0 ? void 0 : relationshipDraft.legalTerms) && id === relationshipDraft.legalTerms.libraryLegalTermsId;
    })) {
      return legalTermsDocuments;
    } else if ((relationshipDraft === null || relationshipDraft === void 0 ? void 0 : relationshipDraft.legalTerms) != null) {
      return legalTermsDocuments.concat([legalTermsModel.fromDraftLegalTerms(relationshipDraft === null || relationshipDraft === void 0 ? void 0 : relationshipDraft.legalTerms)]);
    } else {
      return legalTermsDocuments;
    }
  }, [legalTermsDocuments, relationshipDraft === null || relationshipDraft === void 0 ? void 0 : relationshipDraft.legalTerms]);
  const {
    draftHasPdf,
    isLegalTermsFromLibrary,
    isUploadPdfMode
  } = useMemo(() => {
    const draftHasPdf = !!(relationshipDraft !== null && relationshipDraft !== void 0 && relationshipDraft.legalTerms) && relationshipDraft.legalTerms.type === 'pdf';
    const legalTermsId = (relationshipDraft === null || relationshipDraft === void 0 ? void 0 : relationshipDraft.legalTerms) != null && 'libraryLegalTermsId' in relationshipDraft.legalTerms && relationshipDraft.legalTerms.libraryLegalTermsId;
    const isLegalTermsFromLibrary = legalTermsId && availableLegalDocs.some(doc => doc.id === legalTermsId);
    const isUploadPdfMode = draftHasPdf && !isLegalTermsFromLibrary;
    return {
      draftHasPdf,
      isLegalTermsFromLibrary,
      isUploadPdfMode
    };
  }, [relationshipDraft, availableLegalDocs]);
  const [tosPdfFilename, setTosPdfFilename] = useState(draftHasPdf && (relationshipDraft === null || relationshipDraft === void 0 ? void 0 : (_relationshipDraft$le = relationshipDraft.legalTerms) === null || _relationshipDraft$le === void 0 ? void 0 : _relationshipDraft$le.type) === 'pdf' ? relationshipDraft === null || relationshipDraft === void 0 ? void 0 : (_relationshipDraft$le2 = relationshipDraft.legalTerms) === null || _relationshipDraft$le2 === void 0 ? void 0 : _relationshipDraft$le2.displayName : null);
  const [tosPdfUniqueName, setTosPdfUniqueName] = useState(isUploadPdfMode && (relationshipDraft === null || relationshipDraft === void 0 ? void 0 : (_relationshipDraft$le3 = relationshipDraft.legalTerms) === null || _relationshipDraft$le3 === void 0 ? void 0 : _relationshipDraft$le3.type) === 'pdf' ? relationshipDraft === null || relationshipDraft === void 0 ? void 0 : (_relationshipDraft$le4 = relationshipDraft.legalTerms) === null || _relationshipDraft$le4 === void 0 ? void 0 : _relationshipDraft$le4.pdfUniqueName : null);
  const [tosFileId, setTosFileId] = useState(() => {
    const {
      legalTermsDocumentId
    } = worksAt.settings || {};
    return isLegalTermsFromLibrary && (relationshipDraft === null || relationshipDraft === void 0 ? void 0 : relationshipDraft.legalTerms) != null && 'libraryLegalTermsId' in (relationshipDraft === null || relationshipDraft === void 0 ? void 0 : relationshipDraft.legalTerms) ? relationshipDraft.legalTerms.libraryLegalTermsId : legalTermsDocumentId || null;
  });
  const [amendmentAutoApproval, setAmendmentAutoApproval] = useState(relationshipDraft !== null && relationshipDraft !== void 0 && relationshipDraft.amendmentsApprovalMethod ? amendmentsApprovalMethodModel.isAutoApproval(relationshipDraft.amendmentsApprovalMethod) : !!((_worksAt$settings = worksAt.settings) !== null && _worksAt$settings !== void 0 && _worksAt$settings.amendmentsApprovalMethod) && amendmentsApprovalMethodModel.isAutoApproval(worksAt.settings.amendmentsApprovalMethod));
  const [autoApprovalNoticePeriod, setAutoApprovalNoticePeriod] = useState(relationshipDraft !== null && relationshipDraft !== void 0 && relationshipDraft.amendmentsApprovalMethod && amendmentsApprovalMethodModel.isAutoApproval(relationshipDraft.amendmentsApprovalMethod) ? relationshipDraft.amendmentsApprovalMethod.auto.noticePeriodDays : (_worksAt$settings2 = worksAt.settings) !== null && _worksAt$settings2 !== void 0 && _worksAt$settings2.amendmentsApprovalMethod && amendmentsApprovalMethodModel.isAutoApproval(worksAt.settings.amendmentsApprovalMethod) ? worksAt.settings.amendmentsApprovalMethod.auto.noticePeriodDays : 30);
  const [netTerms, setNetTerms] = useState((relationshipDraft === null || relationshipDraft === void 0 ? void 0 : relationshipDraft.netTerms) != null ? relationshipDraft === null || relationshipDraft === void 0 ? void 0 : relationshipDraft.netTerms : ((_worksAt$settings3 = worksAt.settings) === null || _worksAt$settings3 === void 0 ? void 0 : _worksAt$settings3.netTerms) || 0);
  const form = useForm({
    mode: 'all',
    defaultValues: {
      extraClause: (relationshipDraft === null || relationshipDraft === void 0 ? void 0 : relationshipDraft.extraClause) || ''
    }
  });
  const [extraClause] = form.watch(['extraClause']);
  const [extraClauseValid, setExtraClauseValid] = useState(true);
  const [requirePaymentMethod, setRequirePaymentMethod] = useState(!isSampleRelationship && (relationshipDraft === null || relationshipDraft === void 0 ? void 0 : relationshipDraft.requirePaymentMethod) !== false);
  const [allowManualPayments, setAllowManualPayments] = useState(!!(relationshipDraft !== null && relationshipDraft !== void 0 && relationshipDraft.allowManualPayments));
  const [transactionFees, setTransactionFees] = useState(!!(relationshipDraft ? relationshipDraft.coverCreditCardFees : (_worksAt$settings4 = worksAt.settings) === null || _worksAt$settings4 === void 0 ? void 0 : (_worksAt$settings4$fe = _worksAt$settings4.feeControls) === null || _worksAt$settings4$fe === void 0 ? void 0 : _worksAt$settings4$fe.coverCreditCardFees));
  let initialText;

  if (isExistingCustomer) {
    var _worksAt$settings$int, _worksAt$settings5;

    initialText = (_worksAt$settings$int = (_worksAt$settings5 = worksAt.settings) === null || _worksAt$settings5 === void 0 ? void 0 : _worksAt$settings5.introductoryMessages.invitation) !== null && _worksAt$settings$int !== void 0 ? _worksAt$settings$int : businessSettingsDefaults.introductoryMessages.invitation;
  } else {
    var _worksAt$settings$int2, _worksAt$settings6;

    initialText = (_worksAt$settings$int2 = (_worksAt$settings6 = worksAt.settings) === null || _worksAt$settings6 === void 0 ? void 0 : _worksAt$settings6.introductoryMessages.proposal) !== null && _worksAt$settings$int2 !== void 0 ? _worksAt$settings$int2 : businessSettingsDefaults.introductoryMessages.proposal;
  }

  const [customNotificationText, setCustomNotificationText] = useState((relationshipDraft === null || relationshipDraft === void 0 ? void 0 : relationshipDraft.customNotificationText) || initialText);
  const [showNoServicesWarning, setShowNoServicesWarning] = useState(false);
  const [scrollToServiceId, signalScrollToServiceId] = useSignal();
  const [showInvalidPackagesWarning, setShowInvalidPackagesWarning] = useState(false);
  const [showAgreementSettingsWarning, setShowAgreementSettingsWarning] = useState(false);
  const [showExtraClauseWarning, setShowExtraClauseWarning] = useState(false);
  const [showServiceValidationWarning, setShowServiceValidationWarning] = useState([]);
  const [scrollContainer, setScrollContainer] = useState(null);
  const [showAgreementSettings, setShowAgreementSettings] = useState(false);
  const [showPaymentSettings, setShowPaymentSettings] = useState(false); // Auto-save stuff

  const hasUnsavedChangesRef = useRef(false);
  const autosaveTimerRef = useRef(null);
  const changesSavedTimerRef = useRef(null);
  const [needToSaveDraft, setNeedToSaveDraft] = useState(false);
  const servicePackagesRef = useRef(null);
  const [hasServiceError, setHasServiceError] = useState(false); // accountant access

  const [requestAccountantAccess, setRequestAccountantAccess] = useState(relationshipDraft ? relationshipDraft.requestAccountantAccess : (_worksAt$settings7 = worksAt.settings) === null || _worksAt$settings7 === void 0 ? void 0 : _worksAt$settings7.requestAccountantAccess);
  const hasRequestAccountantAccess = requestAccountantAccessModel.wasRequested(requestAccountantAccess);
  useImperativeHandle(doScrollToTopRef, () => {
    return () => scrollContainer === null || scrollContainer === void 0 ? void 0 : scrollContainer.scrollTo({
      left: 0,
      top: 0,
      behavior: 'smooth'
    });
  }); // If any relevant fields changed, trigger an autosave

  useDidUpdate(() => {
    hasUnsavedChangesRef.current = true;

    if (isMounted() && onAutoSaveCb != null) {
      if (!autosaveTimerRef.current) {
        const triggerSaveDraft = () => isMounted() && setNeedToSaveDraft(true);

        if (autoSaveTimeout === 0) {
          triggerSaveDraft();
        } else {
          autosaveTimerRef.current = setTimeout(triggerSaveDraft, autoSaveTimeout);
        }
      }
    }
  }, [allowManualPayments, amendmentAutoApproval, autoApprovalNoticePeriod, autoSaveTimeout, bundles, coverLetter, customNotificationText, extraClause, includingExpenses, isSampleRelationship, netTerms, requirePaymentMethod, services, tosFileId, tosPdfFilename, tosPdfUniqueName, transactionFees, requestAccountantAccess, ...additionalAutoSaveDependencies]);
  useEffect(() => {
    if (needToSaveDraft && onAutoSaveCb != null && isMounted()) {
      setNeedToSaveDraft(false);
      const data = makeAgreementStudioCommon();

      if (data != null) {
        autosaveTimerRef.current = null;
        onAutoSaveCb(data, isMounted);
        hasUnsavedChangesRef.current = false;
      }
    } else {
      hasUnsavedChangesRef.current = false;
    } // eslint-disable-next-line react-hooks-static-deps/exhaustive-deps

  }, [needToSaveDraft]);
  useEffect(() => {
    document.title = 'Agreement Studio'; // Cleanup timers.

    return () => {
      autosaveTimerRef.current && clearTimeout(autosaveTimerRef.current); // eslint-disable-next-line react-hooks-static-deps/exhaustive-deps

      changesSavedTimerRef.current && clearTimeout(changesSavedTimerRef.current);
    };
  }, []); // Scroll to show the warnings in agreement settings

  useEffect(() => {
    if (showAgreementSettingsWarning) {
      const errorMessageElement = document.querySelector('[data-testid="agreement-studio-customer-details-input-display-title-helper-text"]') || document.querySelector('[data-testid="pdf-file-not-uploaded-error"]') || document.querySelector('[data-testid="formling-extra-clause-input-helper-text"]');

      if (errorMessageElement) {
        setTimeout(() => scrollIntoView(errorMessageElement, {
          disableScrollAnimation: true
        }), 100);
      }
    }
  }, [showAgreementSettingsWarning]);
  const agreementData = useMemo(() => {
    return {
      netTerms,
      autoPayment: !allowManualPayments
    };
  }, [netTerms, allowManualPayments]);

  function makeLegalTerms() {
    if (tosFileId != null) {
      const doc = availableLegalDocs.find(_ref2 => {
        let {
          id
        } = _ref2;
        return id === tosFileId;
      });

      if (doc != null) {
        return legalTermsModel.toDraftLegalTerms(doc, legalTermsDocuments.find(_ref3 => {
          let {
            id
          } = _ref3;
          return id === tosFileId;
        }) == null);
      } else {
        return null;
      }
    } else {
      return null;
    }
  }

  const onOptionalChange = (serviceId, optional, includedByDefault) => {
    applyServicesChange(s => {
      if (s.id === serviceId) {
        return _objectSpread(_objectSpread({}, s), {}, {
          participation: optional ? serviceModel.makeOptionalParticipation(includedByDefault) : serviceModel.makeMandatoryParticipation()
        });
      }

      return s;
    });
  };

  const onLimitBillingTimesChange = (serviceId, enabled, limit) => {
    applyServicesChange(s => {
      if (s.id === serviceId) {
        return serviceModel.setMaxCharges(s, enabled, limit);
      }

      return s;
    });
  };

  const onPausedChange = (serviceId, isPaused) => {
    applyServicesChange(s => {
      if (s.id === serviceId) {
        return serviceModel.setPaused(s, isPaused);
      }

      return s;
    });
  };

  const onBillOnAcceptanceChange = (serviceId, isBillOnAcceptance) => {
    applyServicesChange(s => {
      if (s.id === serviceId) {
        return serviceModel.setSkipBillingOnAcceptance(s, !isBillOnAcceptance);
      }

      return s;
    });
  };

  const onProratedChange = (serviceId, isProrated) => {
    applyServicesChange(s => {
      if (s.id === serviceId) {
        return serviceModel.setProrated(s, isProrated);
      }

      return s;
    });
  };

  function applyServicesChange(fn) {
    if (bundles.length) {
      const newBundles = bundles.map(b => _objectSpread(_objectSpread({}, b), {}, {
        services: b.services.map(fn)
      }));
      setBundles(newBundles);
    } else if (services.length) {
      setServices(services.map(fn));
    }
  }

  if (contacts.error) {
    throw new Error('Failed to fetch contacts list'); // Will trigger the react error-boundary
  }

  const onClose = async () => {
    logBI(10480);

    if (onCloseCb == null || (await onCloseCb(hasUnsavedChangesRef.current))) {
      if (location.pathname === routes.agreementStudio) {
        history.goBack();
      } else if (location.pathname === routes.proposalTemplate(relationshipDraft === null || relationshipDraft === void 0 ? void 0 : relationshipDraft.id)) {
        history.push(routes.proposalTemplates);
      } else {
        history.push(routes.draftsDashboard);
      }
    }
  };

  const makeAgreementStudioCommon = () => {
    const legalTerms = makeLegalTerms();
    if (legalTerms == null) return null;
    return {
      services,
      serviceBundles: {
        bundles
      },
      legalTerms,
      netTerms,
      coverLetter,
      extraClause,
      requirePaymentMethod,
      allowManualPayments,
      coverCreditCardFees: transactionFees,
      isExistingCustomer,
      customNotificationText: customNotificationText,
      amendmentsApprovalMethod: amendmentAutoApproval ? amendmentsApprovalMethodModel.makeAmendmentsAutoApprovalMethod(autoApprovalNoticePeriod || 30) : amendmentsApprovalMethodModel.makeAmendmentsManualApprovalMethod(),
      requestAccountantAccess
    };
  };

  const onSubmit = async () => {
    setWasSubmitted(true);

    while (hasUnsavedChangesRef.current && onAutoSaveCb != null) {
      await wait(300);
    }

    if (!services.length && !bundles.length || bundles.length && bundles.some(b => !b.services.length)) {
      // In case it was true before, this ensures we trigger the useEffect in ServicePackages that switches to the relevant package.
      setShowNoServicesWarning(false);
      setTimeout(() => setShowNoServicesWarning(true));
      scrollRefIntoView(servicePackagesRef);
      return;
    }

    const invalidServices = services.filter(serviceModel.isInvalid).concat(bundles.flatMap(bundle => bundle.services.filter(serviceModel.isInvalid)));

    if (invalidServices.length > 0) {
      signalScrollToServiceId(invalidServices[0].id);
      setShowServiceValidationWarning(invalidServices.map(serviceModel.id));
      return;
    } else {
      setShowServiceValidationWarning([]);
    }

    if (bundles.length === 1) {
      setShowInvalidPackagesWarning(true);
      scrollRefIntoView(servicePackagesRef);
      return;
    }

    if (!coverLetterValid || amendmentAutoApproval && !(autoApprovalNoticePeriod && isAmendmentAutoApprovalValid(autoApprovalNoticePeriod, EXTEND_AMENDMENTS_AUTO_APPROVAL))) {
      setShowAgreementSettings(true);
      setShowAgreementSettingsWarning(true);
      return;
    }

    if (!extraClauseValid) {
      setShowAgreementSettings(true);
      setShowExtraClauseWarning(true);
      return;
    }

    if (hasServiceError) {
      scrollRefIntoView(servicePackagesRef);
      return;
    }

    const data = makeAgreementStudioCommon();

    if (data != null) {
      autosaveTimerRef.current && clearTimeout(autosaveTimerRef.current);
      autosaveTimerRef.current = null;
      onSubmitCb(data);
    }
  };

  function scrollRefIntoView(ref) {
    setTimeout(() => {
      ref.current && scrollIntoView(ref.current, {
        disableScrollAnimation
      });
    }, 100);
  }

  return /*#__PURE__*/React.createElement(FormProvider, form, /*#__PURE__*/React.createElement(FullPageModal, {
    showFooter: true,
    "data-testid": "full-page-agreement-studio",
    onClose: onClose
  }, /*#__PURE__*/React.createElement("div", {
    className: styles.mainContainer,
    "data-testid": "agreement-studio"
  }, /*#__PURE__*/React.createElement(FullPageModalHeaderStart, null, /*#__PURE__*/React.createElement("div", {
    className: styles.modalHeader
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "h5"
  }, modalTitle), /*#__PURE__*/React.createElement("div", {
    className: styles.statusContainer
  }, savingStatusMP))), /*#__PURE__*/React.createElement("div", {
    className: styles.agreementStudio,
    ref: setScrollContainer
  }, /*#__PURE__*/React.createElement(ErrorBoundaryPage, {
    bounded: true
  }, /*#__PURE__*/React.createElement(ScrollToTopButton, {
    scrollContainer: scrollContainer
  }), /*#__PURE__*/React.createElement("div", {
    style: {
      width: '752px'
    }
  }, /*#__PURE__*/React.createElement(FormlingContainer, {
    title: "Agreement details"
  }, /*#__PURE__*/React.createElement(Grid, {
    align: "center",
    rowGap: "small"
  }, agreementTitleMP, /*#__PURE__*/React.createElement(IntroMessage, {
    initialTemplateContent: customNotificationText,
    onMessageChange: newIntroMsg => setCustomNotificationText(newIntroMsg)
  }))), /*#__PURE__*/React.createElement(FormlingContainer, {
    title: "Services provided"
  }, /*#__PURE__*/React.createElement(ServicePackages, {
    showErrors: wasSubmitted,
    showEmptyWarning: showNoServicesWarning,
    scrollToServiceId: scrollToServiceId,
    ref: servicePackagesRef,
    showInvalidPackagesWarning: showInvalidPackagesWarning,
    services: services,
    bundles: bundles,
    agreementData: agreementData,
    showServiceValidationWarning: showServiceValidationWarning,
    setBundles: setBundles,
    setServices: setServices,
    onPausedChange: onPausedChange,
    onBillOnAcceptanceChange: onBillOnAcceptanceChange,
    onProratedChange: onProratedChange,
    onOptionalChange: onOptionalChange,
    onLimitBillingTimesChange: onLimitBillingTimesChange,
    onHasError: setHasServiceError
  })), /*#__PURE__*/React.createElement(FormlingContainer, {
    title: "Payment settings",
    togglePanelView: true,
    "data-testid": "agreement-studio-payment-settings-panel",
    isOpen: showPaymentSettings,
    setIsOpen: setShowPaymentSettings
  }, /*#__PURE__*/React.createElement("div", {
    className: styles.marginBottom
  }, /*#__PURE__*/React.createElement(Typography, {
    as: "p"
  }, "Set the number of days until the payment is due"), /*#__PURE__*/React.createElement(TextField, {
    style: {
      marginBlockStart: 8
    },
    select: true,
    value: netTerms,
    onChange: e => {
      const selectedNetTerms = +e.target.value;
      logBI(10470, {
        selectedNetTerms
      });
      setNetTerms(selectedNetTerms);
    },
    testid: "agreement-studio-net-terms-dropdown",
    width: defaultWidth
  }, netTermsOptions.map((numOfDays, idx) => {
    const value = numOfDays.toString();
    const text = numOfDays === 0 ? 'Upon receipt of invoice' : numberAndUnit(numOfDays, 'day');
    return /*#__PURE__*/React.createElement(MenuItem, {
      key: value,
      value: value,
      "data-testid": "agreement-studio-net-terms-option-".concat(idx)
    }, text);
  }))), /*#__PURE__*/React.createElement(InnerSep, {
    thin: true
  }), /*#__PURE__*/React.createElement(CoverCreditCardFeesSelector, {
    vendorBusinessName: worksAt.name,
    clientBusinessName: contact === null || contact === void 0 ? void 0 : contact.companyName,
    value: transactionFees,
    testid: "agreement-studio-transaction-fees",
    onOpen: () => logBI(10482, {
      selected: transactionFees
    }),
    onChange: selected => {
      logBI(10483, {
        selected
      });
      setTransactionFees(selected);
    }
  }), /*#__PURE__*/React.createElement(InnerSep, {
    thin: true
  }), /*#__PURE__*/React.createElement(WithInfoIcon, {
    info: /*#__PURE__*/React.createElement(Typography, {
      skin: "invertedStandard"
    }, "Select if your client doesn't have a bank account yet, or you don't want us to facilitate payments for now.")
  }, /*#__PURE__*/React.createElement(ConditionalWrapper, {
    condition: isSampleRelationship,
    wrapper: children => /*#__PURE__*/React.createElement(Tooltip, {
      placement: "top",
      title: /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Typography, {
        skin: "invertedStandard",
        weight: "bold"
      }, "Testing Mode Disables Payments"), /*#__PURE__*/React.createElement(Typography, {
        skin: "invertedStandard"
      }, "When creating a proposal with a sample client, this option is disabled. No payment will be processed or saved when you send it, so you can interact with it freely."))
    }, /*#__PURE__*/React.createElement("div", null, children))
  }, /*#__PURE__*/React.createElement(Checkbox, {
    "data-testid": "agreement-studio-require-payment-method-Checkbox",
    checked: !requirePaymentMethod && !isSampleRelationship,
    disabled: isSampleRelationship,
    onChange: () => {
      logBI(requirePaymentMethod ? 10476 : 10475, {});
      setRequirePaymentMethod(!requirePaymentMethod);
    }
  }, /*#__PURE__*/React.createElement(Typography, {
    skin: isSampleRelationship ? 'disabled' : 'standard'
  }, "Let my client approve the agreement without providing a payment method")))), /*#__PURE__*/React.createElement(InnerSep, {
    thin: true
  }), /*#__PURE__*/React.createElement(AllowManualPaymentsCheckbox, {
    isSampleRelationship: isSampleRelationship,
    value: allowManualPayments,
    onChange: setAllowManualPayments
  })), /*#__PURE__*/React.createElement(FormlingContainer, {
    title: "Agreement settings",
    togglePanelView: true,
    isOpen: showAgreementSettings,
    setIsOpen: setShowAgreementSettings,
    "data-testid": "agreement-studio-settings-panel"
  }, /*#__PURE__*/React.createElement(LegalTermsDropdown, {
    label: "Terms & Conditions",
    selectedLegalTerms: availableLegalDocs.find(_ref4 => {
      let {
        id
      } = _ref4;
      return id === tosFileId;
    }),
    legalTerms: availableLegalDocs,
    showViewTosButton: true,
    onUpload: onUploadLegalTermsDocument,
    onChange: id => {
      var _availableLegalDocs$f, _availableLegalDocs$f2;

      setTosFileId(id);
      setTosPdfFilename((_availableLegalDocs$f = availableLegalDocs.find(_ref5 => {
        let {
          id
        } = _ref5;
        return id === tosFileId;
      })) === null || _availableLegalDocs$f === void 0 ? void 0 : _availableLegalDocs$f.name);
      setTosPdfUniqueName((_availableLegalDocs$f2 = availableLegalDocs.filter(legalTermsModel.isPDF).find(doc => doc.id === tosFileId)) === null || _availableLegalDocs$f2 === void 0 ? void 0 : _availableLegalDocs$f2.pdfUniqueName);
    }
  }), /*#__PURE__*/React.createElement(AmendmentsApprovalMethodField, {
    enabled: amendmentAutoApproval,
    noticePeriod: autoApprovalNoticePeriod,
    onToggle: setAmendmentAutoApproval,
    onChangePeriod: setAutoApprovalNoticePeriod
  }), /*#__PURE__*/React.createElement(WithInfoIcon, {
    info: accountantAccessInfoTooltip
  }, /*#__PURE__*/React.createElement(Checkbox, {
    "data-testid": "agreement-studio-accountant-access-checkbox",
    checked: hasRequestAccountantAccess,
    onChange: () => {
      const newVal = hasRequestAccountantAccess ? RequestAccountantAccess.None : COB_IMPROVEMENTS ? RequestAccountantAccess.Mandatory : RequestAccountantAccess.Optional;
      setRequestAccountantAccess(newVal);
    }
  }, /*#__PURE__*/React.createElement(Typography, null, "Request Accountant Access to your client's Anchor account"))), hasRequestAccountantAccess && !COB_IMPROVEMENTS && /*#__PURE__*/React.createElement("div", {
    className: styles.requestAccountantAccess
  }, /*#__PURE__*/React.createElement(Typography, null, "Allow my client to sign the proposal without granting Accountant Access"), /*#__PURE__*/React.createElement(TextField, {
    select: true,
    size: "small",
    testid: "agreement-studio-acc-access-dropdown",
    width: 76,
    value: requestAccountantAccess,
    onChange: e => {
      const newValue = e.target.value;
      setRequestAccountantAccess(newValue);
    }
  }, [{
    value: RequestAccountantAccess.Optional,
    label: 'Yes'
  }, {
    value: RequestAccountantAccess.Mandatory,
    label: 'No'
  }].map(_ref6 => {
    let {
      value,
      label
    } = _ref6;
    return /*#__PURE__*/React.createElement(MenuItem, {
      key: value,
      value: value,
      "data-testid": "agreement-studio-acc-access-".concat(value)
    }, label);
  }))), /*#__PURE__*/React.createElement(ExtraClause, {
    showAgreementSettingsWarning: showExtraClauseWarning,
    onValidation: setExtraClauseValid
  }))))), /*#__PURE__*/React.createElement(FullPageModalFooterStart, null, /*#__PURE__*/React.createElement("div", {
    className: styles.modalFooterStartPhantom
  })), /*#__PURE__*/React.createElement(FullPageModalAdditionalActions, {
    inlineActions: compact(inlineFooterActions),
    dropdownActions: compact(additionalFooterActions)
  }), /*#__PURE__*/React.createElement(FullPageModalFooterEnd, null, /*#__PURE__*/React.createElement(Button, {
    skin: "primaryOutline",
    "data-testid": "agreement-studio-topbar-btn-preview",
    "data-intercom-target": "Preview",
    onClick: () => {
      const data = makeAgreementStudioCommon();
      if (data != null) openPreviewModal(data);
    }
  }, "View client preview"), /*#__PURE__*/React.createElement(Button, {
    "data-testid": "agreement-studio-topbar-btn-submit",
    onClick: onSubmit,
    intent: submitButtonIntent
  }, submitButtonLabel)))));
}